import { Text } from '@chakra-ui/layout';
import {
  FormControl,
  FormErrorMessage,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
} from '@chakra-ui/react';
import Block from '@uiw/react-color-block';
import classNames from 'classnames';
import { type CSSProperties, useState } from 'react';
import NumberFormat from 'react-number-format';

import { FormatType, useChartTheme } from '@revelio/replots';
import {
  Accordion,
  Checkbox,
  Input,
  Popover,
  RadioLabelSize,
  Select,
  Radio as UiRadio,
  RadioGroup as UiRadioGroup,
} from '@revelio/ui';

import { ADAPTIVE_MAP } from '../../hooks/useAdaptiveChartType';
import { usePlotMetricFromItem } from '../../hooks/usePlotMetric';
import { getMetricPaletteConfig } from '../Chart/Map/utils/metric-scales';
import {
  DEFAULT_SANKEY_SHARE_EXPONENT,
  DEFAULT_SANKEY_TOTAL_EXPONENT,
  isDevBuild,
  resolveSankeyWidthScale,
  SANKEY_EXPONENT_MIN,
  SANKEY_EXPONENT_STEP,
} from '../Chart/Sankey/sankeyScale';
import {
  NOTICEABLE_DIFFERENCE_MAX,
  setSankeyNoticeableDifference,
  useSankeyNoticeableDifference,
} from '../Chart/Sankey/sankeyScaleDebug';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import type { ChartType } from '../PageConfigProvider/types/chart';
import type {
  DataNormalization,
  PlotSortDirection,
  PositionedItem,
} from '../PageConfigProvider/types/item';
import { AdaptiveChartSelector } from './AdaptiveChartSelector';
import panelStyles from './ChartBuilderPanel.module.css';
import styles from './ChartCustomizePanel.module.css';
import { ConfigSection } from './ConfigSection';
import layoutStyles from './chart-customize-panel.module.css';
import { DisplayAsSelector } from './DisplayAsSelector';
import { DivergingBarComboSettings } from './DivergingBarComboSettings';
import { DivergingBarSettings } from './DivergingBarSettings';
import { SankeyLabelSettings } from './SankeyLabelSettings';
import labelStyles from './section-label.module.css';

const defaultChartTypeShowDataLabels: Partial<Record<ChartType, boolean>> = {
  bar: true,
  'grouped-bar': false,
  sankey: false,
  'diverging-bar': false,
};

const chartWithSingleColor: ChartType[] = [
  'bar',
  'sankey',
  'volume',
  'map',
  'text',
];

const chartWithColorPalette: ChartType[] = [
  'stacked-bar',
  'grouped-bar',
  'line',
  'distribution',
  'box',
  'gauge-v2',
];

const chartWithColor: ChartType[] = [
  ...chartWithSingleColor,
  ...chartWithColorPalette,
];

const chartWithDataLabels: ChartType[] = [
  'bar',
  'grouped-bar',
  'sankey',
  'diverging-bar',
  'diverging-bar-combo',
];
const chartWithZebraStripe: ChartType[] = ['table'];
const chartWithLineOptions: ChartType[] = ['line'];
const chartWithSortOptions: ChartType[] = ['bar'];
const chartWithHideOther: ChartType[] = [
  'bar',
  'grouped-bar',
  'diverging-bar-combo',
];

const chartWithData: ChartType[] = [
  ...chartWithDataLabels,
  ...chartWithZebraStripe,
];

const chartWithComparisonTransformation: ChartType[] = ['bar', 'diverging-bar'];

const CHART_TYPE_DISPLAY_LABELS: Partial<Record<ChartType, string>> = {
  bar: 'Bar',
  'stacked-bar': 'Stacked bar',
  metric: 'Metric',
  treemap: 'Treemap',
  sankey: 'Sankey',
  chord: 'Chord',
};

const getChartTypeLabel = (chartType: ChartType): string =>
  CHART_TYPE_DISPLAY_LABELS[chartType] ?? chartType;

type SelectOption = { value: string; label: string };

const FORMAT_OPTIONS: SelectOption[] = [
  { value: '', label: 'Default' },
  { value: FormatType.INTEGER, label: 'Integer' },
  { value: FormatType.CURRENCY, label: 'Currency' },
  { value: FormatType.PERCENTAGE, label: 'Percentage' },
  { value: FormatType.DECIMAL, label: 'Decimal' },
  { value: FormatType.RATIO, label: 'Ratio' },
  { value: FormatType.SI, label: 'SI' },
];

const SORT_OPTIONS: SelectOption[] = [
  { value: '', label: 'N/A' },
  { value: 'ASC', label: 'Ascending' },
  { value: 'DESC', label: 'Descending' },
];

const SINGLE_COLOR_SWATCHES = [
  '#65C686',
  '#82FBAA',
  '#2D426A',
  '#5ca9e9',
  '#E53E3E',
  '#ECC94B',
  '#E2E8F0',
  '#FFFFFF',
];

const ChartSectionHeader = ({ label }: { label: string }) => (
  <div className={layoutStyles.sectionHeading}>
    <Text
      className={styles.sectionHeader}
      fontSize="xs"
      fontWeight="700"
      textTransform="uppercase"
      letterSpacing="wider"
    >
      {label}
    </Text>
  </div>
);

type ChartCustomizePanelProps = {
  item: PositionedItem;
  isReport?: boolean;
};

export const ChartCustomizePanel = ({
  item,
  isReport = false,
}: ChartCustomizePanelProps) => {
  const isChartWithAdaptive = (chartType: ChartType | undefined): boolean =>
    chartType !== undefined && ADAPTIVE_MAP[chartType] !== undefined;

  const { dispatch } = useTabContentConfig();
  const { palette } = useChartTheme();
  const id = item?.id;
  const baseChartType = item.plot?.chartType;
  const adaptiveTarget = baseChartType
    ? ADAPTIVE_MAP[baseChartType]
    : undefined;
  const isAdaptiveMode = Boolean(item.plot?.adaptive && adaptiveTarget);
  const color = item?.plot?.color ?? '#5CA9E9';
  const defaultGaugePalette = ['#55A3FF', '#FF646D'];
  const colorPalette =
    item?.plot?.colorPalette ??
    (item?.plot?.chartType === 'gauge-v2' ? defaultGaugePalette : palette);

  const selectedMetric = usePlotMetricFromItem(item);
  const metricConfig = getMetricPaletteConfig(selectedMetric);

  const isDisplayModeEnabled =
    item.plot?.chartType === 'line' &&
    selectedMetric === 'headcount' &&
    !!item.plot?.secondaryDimension &&
    item.plot?.secondaryDimension !== 'timeseries';

  const [distributionLowerFocused, setDistributionLowerFocused] =
    useState(false);
  const [distributionUpperFocused, setDistributionUpperFocused] =
    useState(false);
  const [distributionLowerDraft, setDistributionLowerDraft] = useState('');
  const [distributionUpperDraft, setDistributionUpperDraft] = useState('');

  const defaultShowDataLabels = item.plot?.chartType
    ? defaultChartTypeShowDataLabels[item.plot?.chartType]
    : true;

  const { normalization, transformation } = item.plot?.mapOptions ?? {};
  const normalizationMode =
    normalization ?? metricConfig.normalization ?? 'none';
  const transformMode = transformation ?? metricConfig.transformation ?? 'none';

  const isAdvancedMapOptionsEnabled =
    process.env.ENABLE_MAP_CHART_CUSTOMIZATION === 'true';

  const renderChordOptionsSection = () => (
    <ConfigSection title="Display Options">
      <div className={layoutStyles.fieldStack}>
        <div>
          <Text
            className={labelStyles.sectionLabel}
            fontSize="sm"
            fontWeight="500"
            mb={1}
          >
            Label font size
          </Text>
          <Input
            size="sm"
            wrapperClassName={classNames(
              panelStyles.field,
              panelStyles.compactField,
              styles.narrowField
            )}
            type="number"
            aria-label="Chord label font size"
            value={item.plot?.fontSize ?? 14}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (!Number.isNaN(val)) {
                dispatch({
                  type: 'UPDATE_ITEM_FONT_SIZE',
                  payload: { id, fontSize: val },
                });
              }
            }}
          />
        </div>
      </div>
    </ConfigSection>
  );

  const renderSizeTransformSection = (
    title: string,
    value: 'none' | 'sqrt' | undefined,
    onChange: (sizeTransform: 'none' | 'sqrt') => void
  ) => (
    <ConfigSection title={title}>
      <div className={layoutStyles.settingsStack}>
        <div>
          <Text
            className={labelStyles.sectionLabel}
            fontSize="sm"
            fontWeight="500"
            mb={1}
          >
            Size normalization
          </Text>
          <UiRadioGroup
            className={styles.optionsRadioGroup}
            value={value ?? 'none'}
            onChange={(val) => {
              if (val !== 'none' && val !== 'sqrt') return;
              onChange(val);
            }}
          >
            <UiRadio value="none" label="None" labelFontWeight="normal" />
            <UiRadio
              value="sqrt"
              label="Square Root"
              labelFontWeight="normal"
            />
          </UiRadioGroup>
        </div>
      </div>
    </ConfigSection>
  );

  const renderTreemapOptionsSection = () =>
    renderSizeTransformSection(
      'Treemap options',
      item.plot?.treemap?.sizeTransform,
      (treemapSizeTransform) =>
        dispatch({
          type: 'UPDATE_ITEM_TREEMAP_SIZE_TRANSFORM',
          payload: { id, treemapSizeTransform },
        })
    );

  const sankeyOptions = item.plot?.sankey;
  const sankeyWidthScale = resolveSankeyWidthScale(sankeyOptions);
  const noticeableDifference = useSankeyNoticeableDifference(id);

  const renderSankeyExponentSlider = ({
    label,
    value,
    onChange,
    min = SANKEY_EXPONENT_MIN,
    max = 1,
    step = SANKEY_EXPONENT_STEP,
    format = (next: number) => next.toFixed(2),
  }: {
    label: string;
    value: number;
    onChange: (next: number) => void;
    min?: number;
    max?: number;
    step?: number;
    format?: (next: number) => string;
  }) => (
    <div>
      <Text
        className={labelStyles.sectionLabel}
        fontSize="sm"
        fontWeight="500"
        mb={1}
      >
        {label}
      </Text>
      <div className={layoutStyles.sliderRow}>
        <Slider
          flex={1}
          aria-label={label}
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={onChange}
        >
          <SliderTrack>
            <SliderFilledTrack />
          </SliderTrack>
          <SliderThumb />
        </Slider>
        <Text fontSize="sm" minWidth="2rem">
          {format(value)}
        </Text>
      </div>
    </div>
  );

  const renderSankeyOptionsSection = () => (
    <ConfigSection title="Transformation">
      <div className={layoutStyles.settingsStack}>
        <div>
          <UiRadioGroup
            className={styles.sankeyRadioGroup}
            value={sankeyWidthScale}
            onChange={(val) => {
              if (val !== 'linear' && val !== 'power') return;
              dispatch({
                type: 'UPDATE_ITEM_SANKEY_OPTIONS',
                payload: { id, widthScale: val },
              });
            }}
          >
            <UiRadio value="linear" label="Linear" labelFontWeight="normal" />
            <UiRadio value="power" label="Power" labelFontWeight="normal" />
          </UiRadioGroup>
        </div>
        {sankeyWidthScale === 'power' && (
          <>
            {renderSankeyExponentSlider({
              label: 'Node totals exponent',
              value:
                sankeyOptions?.totalExponent ?? DEFAULT_SANKEY_TOTAL_EXPONENT,
              onChange: (totalExponent) => {
                dispatch({
                  type: 'UPDATE_ITEM_SANKEY_OPTIONS',
                  payload: { id, totalExponent },
                });
              },
            })}
            {renderSankeyExponentSlider({
              label: 'Link shares exponent',
              value:
                sankeyOptions?.shareExponent ?? DEFAULT_SANKEY_SHARE_EXPONENT,
              onChange: (shareExponent) => {
                dispatch({
                  type: 'UPDATE_ITEM_SANKEY_OPTIONS',
                  payload: { id, shareExponent },
                });
              },
            })}
            {renderSankeyExponentSlider({
              label: 'Ignore differences under',
              value: noticeableDifference,
              min: 0,
              max: NOTICEABLE_DIFFERENCE_MAX,
              step: 1,
              format: (next) => `${next}%`,
              onChange: (next) => {
                if (id) setSankeyNoticeableDifference(id, next);
              },
            })}
          </>
        )}
      </div>
    </ConfigSection>
  );

  const renderAdaptiveTargetSections = () => {
    if (!isAdaptiveMode || !adaptiveTarget) return null;
    if (adaptiveTarget === 'chord') {
      return renderChordOptionsSection();
    }
    if (adaptiveTarget === 'treemap') {
      return renderTreemapOptionsSection();
    }
    return null;
  };

  return (
    // The first section starts open: Format, or Data for a table (no Format).
    <Accordion
      allowMultiple
      defaultValue={item.plot?.chartType === 'table' ? ['data'] : ['format']}
      className={classNames(styles.root, layoutStyles.panelBody)}
    >
      {item.plot?.chartType && item.plot.chartType !== 'table' && (
        <ConfigSection title="Format" value="format" withTopBorder>
          <div>
            <Text
              className={labelStyles.sectionLabel}
              fontSize="sm"
              fontWeight="500"
              mb={1}
            >
              Format
            </Text>
            <Select<SelectOption>
              size="sm"
              aria-label="Format"
              isSearchable={false}
              options={FORMAT_OPTIONS}
              value={
                FORMAT_OPTIONS.find(
                  (option) => option.value === (item.plot?.format ?? '')
                ) ?? null
              }
              onChange={(option) => {
                const value = option?.value ?? '';
                dispatch({
                  type: 'UPDATE_ITEM_FORMAT',
                  payload: {
                    id,
                    format: value === '' ? undefined : (value as FormatType),
                  },
                });
              }}
            />
          </div>
        </ConfigSection>
      )}
      {chartWithColor.includes(item.plot?.chartType as ChartType) && (
        <ConfigSection title="Color" withTopBorder>
          {chartWithColorPalette.includes(
            item.plot?.chartType as ChartType
          ) && (
            <div className={layoutStyles.swatchList}>
              {colorPalette.map((color, i) => (
                <Popover
                  key={i}
                  placement="left"
                  className={styles.colorPopover}
                  trigger={
                    <div
                      className={layoutStyles.swatch}
                      style={{ '--swatch-color': color } as CSSProperties}
                    />
                  }
                >
                  <Block
                    color={color}
                    showTriangle={false}
                    colors={[]}
                    onChange={(color) => {
                      const newColorPalette = [
                        ...colorPalette.slice(0, i),
                        color.hex,
                        ...colorPalette.slice(i + 1),
                      ];
                      dispatch({
                        type: 'UPDATE_ITEM_COLOR_PALETTE',
                        payload: { id, colorPalette: newColorPalette },
                      });
                    }}
                  />
                </Popover>
              ))}
            </div>
          )}
          {chartWithSingleColor.includes(item.plot?.chartType as ChartType) && (
            <Block
              color={color}
              showTriangle={false}
              onChange={(color) => {
                dispatch({
                  type: 'UPDATE_ITEM_COLOR',
                  payload: { id, color: color.hex },
                });
              }}
              style={{ width: '100%', boxShadow: 'none' }}
              colors={SINGLE_COLOR_SWATCHES}
            />
          )}
        </ConfigSection>
      )}
      {chartWithData.includes(item.plot?.chartType as ChartType) &&
        !(item.plot?.chartType === 'table' && isReport) && (
          <ConfigSection title="Data" value="data">
            {chartWithDataLabels.includes(
              item.plot?.chartType as ChartType
            ) && (
              <Checkbox
                labelSize="sm"
                checked={
                  item.plot?.showDataLabels === undefined
                    ? defaultShowDataLabels
                    : item.plot?.showDataLabels
                }
                onChange={(e) => {
                  dispatch({
                    type: 'UPDATE_ITEM_SHOW_DATA_LABELS',
                    payload: { id, showDataLabels: e.target.checked },
                  });
                }}
              >
                Show Value Labels
              </Checkbox>
            )}
            {chartWithSortOptions.includes(
              item.plot?.chartType as ChartType
            ) && (
              <div className={layoutStyles.nestedField}>
                <Text
                  className={labelStyles.sectionLabel}
                  fontSize="sm"
                  fontWeight="500"
                  mb={1}
                >
                  Sort
                </Text>
                <Select<SelectOption>
                  size="sm"
                  aria-label="Sort"
                  isSearchable={false}
                  options={SORT_OPTIONS}
                  value={
                    SORT_OPTIONS.find(
                      (option) => option.value === (item.plot?.sort ?? '')
                    ) ?? null
                  }
                  onChange={(option) => {
                    const value = option?.value ?? '';
                    dispatch({
                      type: 'UPDATE_ITEM_SORT',
                      payload: {
                        id,
                        sort:
                          value === '' ? null : (value as PlotSortDirection),
                      },
                    });
                  }}
                />
              </div>
            )}
            {item.plot?.chartType === 'bar' && (
              <div className={layoutStyles.inlineFields}>
                <div>
                  <Text
                    className={labelStyles.sectionLabel}
                    fontSize="sm"
                    fontWeight="500"
                    mb={1}
                  >
                    Min value
                  </Text>
                  <Input
                    size="sm"
                    wrapperClassName={classNames(
                      panelStyles.field,
                      panelStyles.compactField,
                      styles.mediumField
                    )}
                    type="number"
                    step="0.1"
                    aria-label="Bar adjusted min"
                    placeholder="Auto"
                    value={item.plot?.minValue ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      const minValue = value === '' ? undefined : Number(value);
                      if (minValue === undefined || !Number.isNaN(minValue)) {
                        dispatch({
                          type: 'UPDATE_ITEM_BAR_AXIS_RANGE',
                          payload: {
                            id,
                            maxValue: item.plot?.maxValue,
                            minValue,
                          },
                        });
                      }
                    }}
                  />
                </div>
                <div>
                  <Text
                    className={labelStyles.sectionLabel}
                    fontSize="sm"
                    fontWeight="500"
                    mb={1}
                  >
                    Max value
                  </Text>
                  <Input
                    size="sm"
                    wrapperClassName={classNames(
                      panelStyles.field,
                      panelStyles.compactField,
                      styles.mediumField
                    )}
                    type="number"
                    step="0.1"
                    aria-label="Bar max value"
                    placeholder="Auto"
                    value={item.plot?.maxValue ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      const maxValue = value === '' ? undefined : Number(value);
                      if (maxValue === undefined || !Number.isNaN(maxValue)) {
                        dispatch({
                          type: 'UPDATE_ITEM_BAR_AXIS_RANGE',
                          payload: {
                            id,
                            maxValue,
                            minValue: item.plot?.minValue,
                          },
                        });
                      }
                    }}
                  />
                </div>
              </div>
            )}
            {chartWithHideOther.includes(item.plot?.chartType as ChartType) && (
              <div className={layoutStyles.nestedField}>
                <Checkbox
                  labelSize="sm"
                  checked={item.plot?.hideOther ?? false}
                  onChange={(e) => {
                    dispatch({
                      type: 'UPDATE_ITEM_HIDE_OTHER',
                      payload: { id, hideOther: e.target.checked },
                    });
                  }}
                >
                  Hide Other
                </Checkbox>
              </div>
            )}
            {chartWithZebraStripe.includes(
              item.plot?.chartType as ChartType
            ) && (
              <div className={layoutStyles.toggleList}>
                {/* Themed tables force zebra off (Table/TableChart), so the
                        toggle would be a silent no-op there. */}
                <Checkbox
                  labelSize="sm"
                  checked={
                    item.plot?.enableRowSelection === undefined
                      ? true
                      : item.plot?.enableRowSelection
                  }
                  onChange={(e) => {
                    dispatch({
                      type: 'UPDATE_ITEM_ENABLE_ROW_SELECTION',
                      payload: {
                        id,
                        enableRowSelection: e.target.checked,
                      },
                    });
                  }}
                >
                  Enable Row Selection
                </Checkbox>
              </div>
            )}
          </ConfigSection>
        )}
      {chartWithLineOptions.includes(item.plot?.chartType as ChartType) && (
        <ConfigSection title="Display Options">
          <div className={layoutStyles.toggleList}>
            {isDisplayModeEnabled && <DisplayAsSelector item={item} />}
            <Checkbox
              labelSize="sm"
              checked={item.plot?.showDiffLabel ?? false}
              onChange={(e) => {
                dispatch({
                  type: 'UPDATE_ITEM_SHOW_DIFF_LABEL',
                  payload: { id, showDiffLabel: e.target.checked },
                });
              }}
            >
              Show Diff Label
            </Checkbox>
            <Checkbox
              labelSize="sm"
              checked={item.plot?.showLegend ?? false}
              onChange={(e) => {
                dispatch({
                  type: 'UPDATE_ITEM_SHOW_LEGEND',
                  payload: { id, showLegend: e.target.checked },
                });
              }}
            >
              Show Legend
            </Checkbox>
            <div>
              <Text
                className={labelStyles.sectionLabel}
                fontSize="sm"
                fontWeight="500"
                mb={1}
              >
                Curve Smoothness
              </Text>
              <div className={layoutStyles.sliderRow}>
                <Slider
                  flex={1}
                  aria-label="Curve smoothness"
                  min={0}
                  max={1}
                  step={0.1}
                  value={item.plot?.curveSmoothness ?? 0}
                  onChange={(val) => {
                    dispatch({
                      type: 'UPDATE_ITEM_CURVE_SMOOTHNESS',
                      payload: { id, curveSmoothness: val },
                    });
                  }}
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb />
                </Slider>
                <Text fontSize="sm" minWidth="2rem">
                  {Number(item.plot?.curveSmoothness ?? 0).toFixed(1)}
                </Text>
              </div>
            </div>
            <div>
              <Text
                className={labelStyles.sectionLabel}
                fontSize="sm"
                fontWeight="500"
                mb={1}
              >
                Font size
              </Text>
              <Input
                size="sm"
                wrapperClassName={classNames(
                  panelStyles.field,
                  panelStyles.compactField,
                  styles.narrowField
                )}
                type="number"
                aria-label="Font size"
                value={item.plot?.fontSize ?? 11}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (!Number.isNaN(val)) {
                    dispatch({
                      type: 'UPDATE_ITEM_FONT_SIZE',
                      payload: { id, fontSize: val },
                    });
                  }
                }}
              />
            </div>
          </div>
        </ConfigSection>
      )}
      {(item.plot?.chartType === 'grouped-bar' ||
        item.plot?.chartType === 'stacked-bar') && (
        <ConfigSection title="Display Options">
          <div>
            <Text
              className={labelStyles.sectionLabel}
              fontSize="sm"
              fontWeight="500"
              mb={1}
            >
              Orientation
            </Text>
            <UiRadioGroup
              className={layoutStyles.toggleList}
              value={item.plot?.orientation ?? 'horizontal'}
              onChange={(val) => {
                if (val !== 'horizontal' && val !== 'vertical') return;
                dispatch({
                  type: 'UPDATE_ITEM_ORIENTATION',
                  payload: { id, orientation: val },
                });
              }}
            >
              <UiRadio
                value="horizontal"
                label="Horizontal"
                labelFontWeight="normal"
                size={RadioLabelSize.SM}
              />
              <UiRadio
                value="vertical"
                label="Vertical"
                labelFontWeight="normal"
                size={RadioLabelSize.SM}
              />
            </UiRadioGroup>
          </div>
          <Checkbox
            className={layoutStyles.spacedCheckbox}
            labelSize="sm"
            checked={item.plot?.showLegend ?? false}
            onChange={(e) => {
              dispatch({
                type: 'UPDATE_ITEM_SHOW_LEGEND',
                payload: { id, showLegend: e.target.checked },
              });
            }}
          >
            Show Legend
          </Checkbox>
        </ConfigSection>
      )}
      {item.plot?.chartType === 'chord' && renderChordOptionsSection()}
      {item.plot?.chartType === 'treemap' && renderTreemapOptionsSection()}
      {item.plot?.chartType === 'icicle' &&
        item.plot.secondaryDimension === 'subsidiaries' &&
        renderSizeTransformSection(
          'Icicle options',
          item.plot.icicle?.sizeTransform,
          (icicleSizeTransform) =>
            dispatch({
              type: 'UPDATE_ITEM_ICICLE_SIZE_TRANSFORM',
              payload: { id, icicleSizeTransform },
            })
        )}
      {item.plot?.chartType === 'sankey' &&
        isDevBuild() &&
        renderSankeyOptionsSection()}
      {item.plot?.chartType === 'distribution' && (
        <ConfigSection title="Distribution Range">
          <FormControl
            isInvalid={(() => {
              const lower = item.plot?.distributionCutoffLower ?? 1;
              const upper = item.plot?.distributionCutoffUpper ?? 99;
              return (
                lower >= upper ||
                lower < 0 ||
                lower > 100 ||
                upper < 0 ||
                upper > 100
              );
            })()}
          >
            <div className={layoutStyles.fieldStack}>
              <div>
                <Text
                  className={labelStyles.sectionLabel}
                  fontSize="sm"
                  fontWeight="500"
                  mb={1}
                >
                  Lower cutoff
                </Text>
                <NumberFormat
                  customInput={Input}
                  size="sm"
                  width="6rem"
                  suffix="%"
                  decimalScale={0}
                  thousandSeparator={false}
                  allowNegative={false}
                  aria-label="Lower cutoff percent"
                  value={
                    distributionLowerFocused
                      ? distributionLowerDraft
                      : String(item.plot?.distributionCutoffLower ?? 1)
                  }
                  onFocus={() => {
                    setDistributionLowerFocused(true);
                    setDistributionLowerDraft(
                      String(item.plot?.distributionCutoffLower ?? 1)
                    );
                  }}
                  onBlur={() => {
                    setDistributionLowerFocused(false);
                    const trimmed = distributionLowerDraft.trim();
                    if (trimmed === '') {
                      dispatch({
                        type: 'UPDATE_ITEM_DISTRIBUTION_CUTOFF',
                        payload: {
                          id,
                          distributionCutoffLower: undefined,
                          distributionCutoffUpper:
                            item.plot?.distributionCutoffUpper,
                        },
                      });
                      return;
                    }
                    const num = Number(trimmed);
                    if (!Number.isNaN(num)) {
                      const clamped = Math.min(100, Math.max(0, num));
                      dispatch({
                        type: 'UPDATE_ITEM_DISTRIBUTION_CUTOFF',
                        payload: {
                          id,
                          distributionCutoffLower: clamped,
                          distributionCutoffUpper:
                            item.plot?.distributionCutoffUpper,
                        },
                      });
                    }
                  }}
                  onValueChange={(values) => {
                    setDistributionLowerDraft(values.value ?? '');
                  }}
                />
              </div>
              <div>
                <Text
                  className={labelStyles.sectionLabel}
                  fontSize="sm"
                  fontWeight="500"
                  mb={1}
                >
                  Upper cutoff
                </Text>
                <NumberFormat
                  customInput={Input}
                  size="sm"
                  width="6rem"
                  suffix="%"
                  decimalScale={0}
                  thousandSeparator={false}
                  allowNegative={false}
                  aria-label="Upper cutoff percent"
                  value={
                    distributionUpperFocused
                      ? distributionUpperDraft
                      : String(item.plot?.distributionCutoffUpper ?? 99)
                  }
                  onFocus={() => {
                    setDistributionUpperFocused(true);
                    setDistributionUpperDraft(
                      String(item.plot?.distributionCutoffUpper ?? 99)
                    );
                  }}
                  onBlur={() => {
                    setDistributionUpperFocused(false);
                    const trimmed = distributionUpperDraft.trim();
                    if (trimmed === '') {
                      dispatch({
                        type: 'UPDATE_ITEM_DISTRIBUTION_CUTOFF',
                        payload: {
                          id,
                          distributionCutoffLower:
                            item.plot?.distributionCutoffLower,
                          distributionCutoffUpper: undefined,
                        },
                      });
                      return;
                    }
                    const num = Number(trimmed);
                    if (!Number.isNaN(num)) {
                      const clamped = Math.min(100, Math.max(0, num));
                      dispatch({
                        type: 'UPDATE_ITEM_DISTRIBUTION_CUTOFF',
                        payload: {
                          id,
                          distributionCutoffLower:
                            item.plot?.distributionCutoffLower,
                          distributionCutoffUpper: clamped,
                        },
                      });
                    }
                  }}
                  onValueChange={(values) => {
                    setDistributionUpperDraft(values.value ?? '');
                  }}
                />
              </div>
            </div>
            <FormErrorMessage mt={2}>
              Values must be between 0 and 100; lower must be less than upper.
            </FormErrorMessage>
          </FormControl>
        </ConfigSection>
      )}
      {isChartWithAdaptive(item.plot?.chartType) && (
        <ConfigSection title="Adaptive">
          <AdaptiveChartSelector item={item} />
        </ConfigSection>
      )}
      {chartWithComparisonTransformation.includes(
        item.plot?.chartType as ChartType
      ) && (
        <ConfigSection title="Comparison">
          <div className={layoutStyles.toggleList}>
            <Checkbox
              labelSize="sm"
              checked={!!item.plot?.comparisonTransformation}
              onChange={(e) => {
                if (e.target.checked) {
                  dispatch({
                    type: 'UPDATE_ITEM_COMPARISON_TRANSFORMATION',
                    payload: {
                      id,
                      comparisonTransformation: {
                        baseline: 'second',
                      },
                    },
                  });
                } else {
                  dispatch({
                    type: 'UPDATE_ITEM_COMPARISON_TRANSFORMATION',
                    payload: {
                      id,
                      comparisonTransformation: undefined,
                    },
                  });
                }
              }}
            >
              Show Difference
            </Checkbox>
          </div>
        </ConfigSection>
      )}
      {item.plot?.chartType === 'diverging-bar' && (
        <DivergingBarSettings item={item} dispatch={dispatch} />
      )}
      {item.plot?.chartType === 'diverging-bar-combo' && (
        <DivergingBarComboSettings item={item} dispatch={dispatch} />
      )}
      {item.plot?.chartType === 'sankey' && (
        <SankeyLabelSettings item={item} dispatch={dispatch} />
      )}
      {(item.plot?.chartType === 'box' ||
        item.plot?.chartType === 'distribution') && (
        <ConfigSection title="Display Options">
          <Checkbox
            labelSize="sm"
            checked={item.plot?.showQuartiles ?? false}
            onChange={(e) => {
              dispatch({
                type: 'UPDATE_ITEM_SHOW_QUARTILES',
                payload: { id, showQuartiles: e.target.checked },
              });
            }}
          >
            Show interquartile range
          </Checkbox>
        </ConfigSection>
      )}
      {isAdvancedMapOptionsEnabled && item.plot?.chartType === 'map' && (
        <>
          <ConfigSection title="Normalization">
            <UiRadioGroup
              className={layoutStyles.radioList}
              value={normalizationMode}
              onChange={(val) => {
                dispatch({
                  type: 'UPDATE_ITEM_NORMALIZATION',
                  payload: {
                    id,
                    normalization: val as DataNormalization,
                  },
                });
              }}
            >
              <UiRadio
                value="none"
                label="None (raw count)"
                labelFontWeight="normal"
                size={RadioLabelSize.SM}
              />
              <UiRadio
                value="area_density"
                label="By geographic area"
                labelFontWeight="normal"
                size={RadioLabelSize.SM}
              />
            </UiRadioGroup>
          </ConfigSection>
          <ConfigSection title="Transformation">
            <UiRadioGroup
              className={layoutStyles.radioList}
              value={transformMode}
              onChange={(val) => {
                dispatch({
                  type: 'UPDATE_ITEM_TRANSFORMATION',
                  payload: {
                    id,
                    transformation: val as 'none' | 'sqrt' | 'log',
                  },
                });
              }}
            >
              <UiRadio
                value="none"
                label="None"
                labelFontWeight="normal"
                size={RadioLabelSize.SM}
              />
              <UiRadio
                value="sqrt"
                label="Square Root"
                labelFontWeight="normal"
                size={RadioLabelSize.SM}
              />
              <UiRadio
                value="log"
                label="Logarithmic"
                labelFontWeight="normal"
                size={RadioLabelSize.SM}
              />
            </UiRadioGroup>
          </ConfigSection>
        </>
      )}
      {isAdaptiveMode && adaptiveTarget && (
        <>
          <ChartSectionHeader
            label={`${getChartTypeLabel(adaptiveTarget)} settings`}
          />
          {renderAdaptiveTargetSections()}
        </>
      )}
    </Accordion>
  );
};
